import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import billingService from '../services/billingService';
import UpgradeModal from '../components/UpgradeModal';
import AuthModal from '../components/AuthModal';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const BillingPage = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    authLoading,
    sessionUser,
    billingStatus,
    billingLoading,
    fetchBillingStatus,
  } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBillingStatus();
    }
  }, [isAuthenticated, fetchBillingStatus]);

  const planLabel = sessionUser?.plan_type === 'pro'
    ? 'Pro'
    : sessionUser?.plan_type === 'team'
      ? 'Team'
      : 'Free';

  const statusBadge = useMemo(() => {
    if (!billingStatus?.subscription_status) return null;
    const status = billingStatus.subscription_status;
    const labelMap = {
      active: 'Active',
      trialing: 'Trial',
      past_due: 'Past due',
      unpaid: 'Unpaid',
      canceled: 'Canceled',
      incomplete: 'Incomplete',
      incomplete_expired: 'Incomplete',
      free: 'Free',
      unknown: 'Unknown',
    };
    const label = labelMap[status] || status;
    let className = 'border border-[rgba(32,30,29,0.12)] bg-sand-100 text-sand-700';
    if (status === 'active') className = 'border border-sage-500/30 bg-sage-100 text-sage-800';
    if (status === 'trialing') className = 'border border-bill-500/30 bg-bill-100 text-bill-700';
    if (['past_due', 'unpaid', 'incomplete', 'incomplete_expired', 'canceled'].includes(status)) {
      className = 'border border-red-500/30 bg-red-100 text-red-700';
    }
    return { label, className };
  }, [billingStatus]);

  const billingIssueMessage = useMemo(() => {
    if (!billingStatus?.subscription_status) return null;
    const status = billingStatus.subscription_status;
    const messageMap = {
      past_due: 'Payment failed. Update your payment method to restore Pro features.',
      unpaid: 'Payment is overdue. Update billing to restore Pro features.',
      incomplete: 'Subscription setup is incomplete. Finish payment to activate Pro.',
      incomplete_expired: 'Subscription setup expired. Restart checkout to upgrade.',
      canceled: 'Subscription canceled. Upgrade again to restore Pro features.',
    };
    return messageMap[status] || null;
  }, [billingStatus]);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const session = await billingService.createPortalSession();
      window.location.href = session.url;
    } catch (error) {
      console.error('Unable to open billing portal:', error);
    } finally {
      setPortalLoading(false);
    }
  };

  if (!authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white shadow-brand-sm p-8 text-center">
          <h1 className="text-2xl font-semibold text-ink">Sign in to manage billing</h1>
          <p className="mt-3 text-sm text-sand-700">
            Create a free account or sign in to upgrade your plan.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => setShowAuthModal(true)}
              className="rounded-full bg-bill-500 px-5 py-2 text-sm font-semibold text-white hover:bg-bill-600 shadow-brand-sm"
            >
              Sign in / Create account
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-full border border-[rgba(32,30,29,0.18)] bg-white px-5 py-2 text-sm font-semibold text-ink hover:bg-sand-100"
            >
              Back to landing
            </button>
          </div>
        </div>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onAuthSuccess={() => navigate('/billing')} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="mx-auto max-w-5xl px-6 py-10">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 text-sm text-sand-700 hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </button>

        <div className="mt-6 rounded-3xl border border-[rgba(32,30,29,0.12)] bg-white shadow-brand-sm p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-bill-100 p-2">
                  <CreditCard className="h-5 w-5 text-bill-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold">Billing</h1>
                  <p className="text-sm text-sand-600">Manage your plan and invoices.</p>
                </div>
              </div>
            </div>
            {statusBadge && (
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
            )}
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-sand-100 p-5">
              <div className="text-xs uppercase text-sand-600">Current plan</div>
              <div className="mt-2 text-2xl font-semibold">{planLabel}</div>
              <p className="mt-2 text-sm text-sand-700">
                {planLabel === 'Free'
                  ? 'Upgrade to Pro to unlock higher limits and automation.'
                  : 'Thanks for supporting DuckPilot!'}
              </p>
            </div>
            <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-sand-100 p-5">
              <div className="text-xs uppercase text-sand-600">Next billing date</div>
              <div className="mt-2 text-lg font-semibold">
                {formatDate(billingStatus?.current_period_end)}
              </div>
              <p className="mt-2 text-sm text-sand-700">
                {billingStatus?.subscription_status === 'active'
                  ? 'Your subscription renews automatically.'
                  : 'No upcoming renewal on file.'}
              </p>
            </div>
          </div>

          {billingIssueMessage && (
            <div className="mt-6 rounded-2xl border border-red-500/30 bg-red-100 p-4 text-sm text-red-700">
              {billingIssueMessage}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {planLabel === 'Free' ? (
              <button
                type="button"
                onClick={() => setShowUpgradeModal(true)}
                className="rounded-full bg-bill-500 px-5 py-2 text-sm font-semibold text-white hover:bg-bill-600 shadow-brand-sm"
              >
                Upgrade to Pro
              </button>
            ) : (
              <button
                type="button"
                onClick={handleOpenPortal}
                disabled={portalLoading}
                className="rounded-full bg-bill-500 px-5 py-2 text-sm font-semibold text-white hover:bg-bill-600 shadow-brand-sm disabled:opacity-50"
              >
                {portalLoading ? 'Opening billing...' : 'Manage billing'}
              </button>
            )}
            {billingLoading && (
              <span className="text-xs text-sand-600">Syncing billing status...</span>
            )}
          </div>

          <div className="mt-6 rounded-2xl border border-[rgba(32,30,29,0.12)] bg-sand-100 p-4 text-xs text-sand-600">
            Need help with billing? Email sydaffn@gmail.com.
          </div>
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onRequireAuth={() => setShowAuthModal(true)}
      />
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onAuthSuccess={() => navigate('/billing')} />
    </div>
  );
};

export default BillingPage;
